// A trip's rows (GET /trips/:id/bundle) as the screens use them: the same read model as PFStore's
// TripPlan and Ledger on the apps (days with stop IDs, lists, places, bookings, money).
import type { RowSnapshot } from '@pf/api-types';
import { convert } from '@pf/money';
import { compareOrder } from '@pf/sync-rules';
import { tripNow } from '../clock.ts';

type V = Record<string, unknown>;
const str = (v: unknown) => (typeof v === 'string' ? v : null);
const num = (v: unknown) => (typeof v === 'number' ? v : null);

export interface Place {
  id: string;
  name: string;
  localName: string | null;
  lat: number | null;
  lng: number | null;
  address: string | null;
  category: string | null;
  openingHours: string | null;
  photoUrl: string | null;
}

export interface Item {
  id: string;
  title: string;
  kind: string;
  dayId: string | null;
  listId: string | null;
  parentItemId: string | null;
  sortKey: string;
  startLocal: string | null;
  endLocal: string | null;
  tz: string | null;
  durationMin: number | null;
  place: Place | null;
}

export interface Planned {
  item: Item;
  stopId: string | null;
  subItems: Planned[];
}

export interface Day {
  id: string;
  date: string;
  title: string | null;
  number: number;
  count: number;
  tz: string | null;
  destination: { name: string; localCurrency: string | null; tz: string | null } | null;
  items: Planned[];
}

export interface Reservation {
  id: string;
  itemId: string | null;
  kind: string;
  confirmation: string | null;
  startLocal: string | null;
  startTz: string | null;
  endLocal: string | null;
  endTz: string | null;
  details: Record<string, string>;
}

export interface Share {
  id: string;
  userId: string;
  amountMinor: number;
  method: string | null;
  value: string | null;
}

export interface Entry {
  id: string;
  ownerUserId: string | null;
  description: string;
  amountMinor: number;
  currency: string;
  categoryId: string | null;
  dateLocal: string | null;
  timeLocal: string | null;
  status: string;
  dueDate: string | null;
  itemId: string | null;
  rates: Record<string, string>;
  payers: Share[];
  splits: Share[];
  instalments: { id: string; amountMinor: number; dueDate: string | null; paidAt: string | null }[];
}

export interface Settlement {
  id: string;
  fromUserId: string;
  toUserId: string;
  amountMinor: number;
  currency: string;
  dateLocal: string | null;
  rates: Record<string, string>;
}

export interface Goal {
  id: string;
  ownerUserId: string | null;
  categoryId: string | null;
  amountMinor: number;
  currency: string;
  period: string;
}

export interface Attachment {
  id: string;
  ownerUserId: string | null;
  ownerType: string;
  ownerId: string | null;
  filename: string;
  mime: string | null;
  size: number | null;
  uploadedAt: string | null;
}

export interface Checklist {
  id: string;
  ownerUserId: string | null;
  title: string;
  sortKey: string;
  entries: { id: string; text: string; checked: boolean; sortKey: string; dueDate: string | null }[];
}

export interface TripModel {
  id: string;
  title: string;
  startDate: string | null;
  endDate: string | null;
  days: Day[];
  lists: { id: string; name: string; kind: string; items: Planned[] }[];
  notes: {
    id: string;
    title: string;
    body: string;
    sortKey: string;
    targetType: string;
    targetId: string | null;
    ownerUserId: string | null;
  }[];
  reservations: Reservation[];
  entries: Entry[];
  settlements: Settlement[];
  goals: Goal[];
  attachments: Attachment[];
  checklists: Checklist[];
  /** The trip's own expense categories (FR-BUD-03); mine-only ones come only to me. */
  categories: {
    id: string;
    name: string;
    icon: string | null;
    ownerUserId: string | null;
    sortKey: string;
  }[];
  visits: {
    id: string;
    userId: string;
    dateLocal: string | null;
    itemId: string | null;
    arriveAt: string | null;
    departAt: string | null;
  }[];
  segments: {
    id: string;
    userId: string;
    dateLocal: string | null;
    mode: string;
    distanceM: number;
    polyline: string;
  }[];
  destinations: {
    id: string;
    name: string;
    localCurrency: string | null;
    tz: string | null;
    startDate: string | null;
    endDate: string | null;
  }[];
}

export const CATEGORIES: Record<string, string> = {
  food: 'Food & drink',
  transport: 'Transport',
  lodging: 'Lodging',
  activities: 'Activities',
  shopping: 'Shopping',
  fees: 'Fees',
  other: 'Other',
};

/** A category's name: built in, the trip's own, else Other. */
export function categoryName(m: TripModel, id: string | null | undefined): string {
  if (!id) return 'Other';
  return CATEGORIES[id] ?? m.categories.find((c) => c.id === id)?.name ?? 'Other';
}

export function tripModel(rows: RowSnapshot[]): TripModel | null {
  const live = rows.filter((r) => r.values.deletedAt == null);
  const by = (table: string) => live.filter((r) => r.table === table);
  const trip = by('trip')[0];
  if (!trip) return null;
  const v = (r: RowSnapshot) => r.values as V;

  const destinations = by('destination').map((r) => ({
    id: r.id,
    name: str(v(r).name) ?? '',
    localCurrency: str(v(r).localCurrency),
    tz: str(v(r).tz),
    startDate: str(v(r).startDate),
    endDate: str(v(r).endDate),
  }));
  const places = new Map<string, Place>(
    by('place').map((r) => {
      let localName: string | null = null;
      try {
        const names = JSON.parse(str(v(r).namesJson) ?? '{}') as Record<string, string>;
        if (names.local && names.local !== v(r).name) localName = names.local;
      } catch {}
      return [
        r.id,
        {
          id: r.id,
          name: str(v(r).name) ?? '',
          localName,
          lat: num(v(r).lat),
          lng: num(v(r).lng),
          address: str(v(r).address),
          category: str(v(r).category),
          openingHours: str(v(r).openingHours),
          photoUrl: str(v(r).photoUrl),
        },
      ];
    }),
  );
  const items: Item[] = by('item').map((r) => ({
    id: r.id,
    title: str(v(r).title) ?? '',
    kind: str(v(r).kind) ?? 'place',
    dayId: str(v(r).dayId),
    listId: str(v(r).listId),
    parentItemId: str(v(r).parentItemId),
    sortKey: str(v(r).sortKey) ?? 'a0',
    startLocal: str(v(r).startLocal),
    endLocal: str(v(r).endLocal),
    tz: str(v(r).tz),
    durationMin: num(v(r).durationMin),
    place: places.get(str(v(r).placeId) ?? '') ?? null,
  }));
  items.sort((a, b) => compareOrder(a, b));
  const children = new Map<string, Item[]>();
  for (const i of items)
    if (i.parentItemId) children.set(i.parentItemId, [...(children.get(i.parentItemId) ?? []), i]);
  const planned = (list: Item[], dayNumber: number | null): Planned[] =>
    list.map((item, index) => {
      const stop = dayNumber === null ? null : `${String(dayNumber).padStart(2, '0')}-${index + 1}`;
      return {
        item,
        stopId: stop,
        subItems: (children.get(item.id) ?? []).map((sub, j) => ({
          item: sub,
          stopId: stop ? `${stop}${String.fromCharCode(97 + Math.min(j, 25))}` : null,
          subItems: [],
        })),
      };
    });

  const dayRows = by('day')
    .map((r) => ({
      id: r.id,
      date: str(v(r).date) ?? '',
      title: str(v(r).title),
      tz: str(v(r).tz),
      destinationId: str(v(r).destinationId),
      sortKey: str(v(r).sortKey) ?? 'a0',
    }))
    .sort((a, b) => a.date.localeCompare(b.date) || compareOrder(a, b));
  const top = items.filter((i) => !i.parentItemId);
  const days: Day[] = dayRows.map((d, i) => {
    const dest =
      destinations.find((x) => x.id === d.destinationId) ??
      destinations.find((x) => (x.startDate ?? '') <= d.date && d.date <= (x.endDate ?? '9999'));
    return {
      id: d.id,
      date: d.date,
      title: d.title,
      number: i + 1,
      count: dayRows.length,
      tz: d.tz ?? dest?.tz ?? null,
      destination: dest ? { name: dest.name, localCurrency: dest.localCurrency, tz: dest.tz } : null,
      items: planned(
        top.filter((x) => x.dayId === d.id),
        i + 1,
      ),
    };
  });
  const lists = by('list')
    .map((r) => ({
      id: r.id,
      name: str(v(r).name) ?? '',
      kind: str(v(r).kind) ?? 'custom',
      sortKey: str(v(r).sortKey) ?? 'a0',
    }))
    .sort(
      (a, b) => (a.kind === 'unscheduled' ? 1 : 0) - (b.kind === 'unscheduled' ? 1 : 0) || compareOrder(a, b),
    )
    .map((l) => ({
      ...l,
      items: planned(
        top.filter((x) => x.listId === l.id),
        null,
      ),
    }));

  const reservations: Reservation[] = by('reservation')
    .map((r) => {
      let details: Record<string, string> = {};
      try {
        details = JSON.parse(str(v(r).detailsJson) ?? '{}') as Record<string, string>;
      } catch {}
      return {
        id: r.id,
        itemId: str(v(r).itemId),
        kind: str(v(r).kind) ?? 'other',
        confirmation: str(v(r).confirmation),
        startLocal: str(v(r).startLocal),
        startTz: str(v(r).startTz),
        endLocal: str(v(r).endLocal),
        endTz: str(v(r).endTz),
        details,
      };
    })
    .filter((r) => items.some((i) => i.id === r.itemId))
    .sort((a, b) => (a.startLocal ?? '9999').localeCompare(b.startLocal ?? '9999'));

  const rates = new Map<string, Record<string, string>>();
  for (const r of by('expenseRate').sort((a, b) =>
    String(a.values.createdAt ?? '').localeCompare(String(b.values.createdAt ?? '')),
  )) {
    const id = str(v(r).expenseId) ?? '';
    rates.set(id, { ...(rates.get(id) ?? {}), [str(v(r).toCurrency) ?? '']: str(v(r).rate) ?? '1' });
  }
  const sharesOf = (table: string) => {
    const out = new Map<string, Share[]>();
    for (const r of by(table)) {
      const id = str(v(r).expenseId) ?? '';
      out.set(id, [
        ...(out.get(id) ?? []),
        {
          id: r.id,
          userId: str(v(r).userId) ?? '',
          amountMinor: num(v(r).amountMinor) ?? 0,
          method: str(v(r).method),
          value: str(v(r).value),
        },
      ]);
    }
    return out;
  };
  const payers = sharesOf('expensePayer');
  const splits = sharesOf('expenseSplit');
  const instalments = new Map<string, Entry['instalments']>();
  for (const r of by('instalment').sort(compareRows)) {
    const id = str(v(r).expenseId) ?? '';
    instalments.set(id, [
      ...(instalments.get(id) ?? []),
      {
        id: r.id,
        amountMinor: num(v(r).amountMinor) ?? 0,
        dueDate: str(v(r).dueDate),
        paidAt: str(v(r).paidAt),
      },
    ]);
  }
  const entries: Entry[] = by('expense')
    .map((r) => ({
      id: r.id,
      ownerUserId: r.ownerUserId,
      description: str(v(r).description) ?? '',
      amountMinor: num(v(r).amountMinor) ?? 0,
      currency: str(v(r).currency) ?? 'EUR',
      categoryId: str(v(r).categoryId),
      dateLocal: str(v(r).dateLocal),
      timeLocal: str(v(r).timeLocal),
      status: str(v(r).status) ?? 'paid',
      dueDate: str(v(r).dueDate),
      itemId: str(v(r).itemId),
      rates: rates.get(r.id) ?? {},
      payers: payers.get(r.id) ?? [],
      splits: splits.get(r.id) ?? [],
      instalments: instalments.get(r.id) ?? [],
    }))
    .sort((a, b) =>
      `${b.dateLocal ?? ''}${b.timeLocal ?? ''}`.localeCompare(`${a.dateLocal ?? ''}${a.timeLocal ?? ''}`),
    );
  const settlements: Settlement[] = by('settlement').map((r) => ({
    id: r.id,
    fromUserId: str(v(r).fromUserId) ?? '',
    toUserId: str(v(r).toUserId) ?? '',
    amountMinor: num(v(r).amountMinor) ?? 0,
    currency: str(v(r).currency) ?? 'EUR',
    dateLocal: str(v(r).dateLocal),
    rates: rates.get(r.id) ?? {},
  }));
  const goals: Goal[] = by('budget').map((r) => ({
    id: r.id,
    ownerUserId: r.ownerUserId,
    categoryId: str(v(r).categoryId),
    amountMinor: num(v(r).amountMinor) ?? 0,
    currency: str(v(r).currency) ?? 'EUR',
    period: str(v(r).period) ?? 'trip',
  }));
  const notes = by('note').map((r) => ({
    id: r.id,
    title: str(v(r).title) ?? '',
    body: str(v(r).body) ?? '',
    sortKey: str(v(r).sortKey) ?? 'a0',
    targetType: str(v(r).targetType) ?? 'trip',
    targetId: str(v(r).targetId),
    ownerUserId: r.ownerUserId,
  }));
  const attachments: Attachment[] = by('attachment').map((r) => ({
    id: r.id,
    ownerUserId: r.ownerUserId,
    ownerType: str(v(r).ownerType) ?? 'trip',
    ownerId: str(v(r).ownerId),
    filename: str(v(r).filename) ?? '',
    mime: str(v(r).mime),
    size: num(v(r).size),
    uploadedAt: str(v(r).uploadedAt),
  }));
  const checklists: Checklist[] = by('checklist')
    .map((r) => ({
      id: r.id,
      ownerUserId: r.ownerUserId,
      title: str(v(r).title) ?? '',
      sortKey: str(v(r).sortKey) ?? 'a0',
      entries: by('checklistEntry')
        .filter((e) => v(e).checklistId === r.id)
        .map((e) => ({
          id: e.id,
          text: str(v(e).text) ?? '',
          checked: v(e).checked === true || v(e).checked === 1,
          sortKey: str(v(e).sortKey) ?? 'a0',
          dueDate: str(v(e).dueDate),
        }))
        .sort(compareOrder),
    }))
    .sort(compareOrder);
  const visits = by('trackStop').map((r) => ({
    id: r.id,
    userId: str(v(r).userId) ?? '',
    dateLocal: str(v(r).dateLocal),
    itemId: str(v(r).itemId),
    arriveAt: str(v(r).arriveAt),
    departAt: str(v(r).departAt),
  }));
  const categories = by('category')
    .map((r) => ({
      id: r.id,
      name: str(v(r).name) ?? '',
      icon: str(v(r).icon),
      ownerUserId: r.ownerUserId ?? null,
      sortKey: str(v(r).sortKey) ?? 'a0',
    }))
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  const segments = by('trackSegment').map((r) => ({
    id: r.id,
    userId: str(v(r).userId) ?? '',
    dateLocal: str(v(r).dateLocal),
    mode: str(v(r).mode) ?? 'unknown',
    distanceM: num(v(r).distanceM) ?? 0,
    polyline: str(v(r).polyline) ?? '',
  }));
  return {
    id: trip.id,
    title: str(v(trip).title) ?? '',
    startDate: str(v(trip).startDate),
    endDate: str(v(trip).endDate),
    days,
    lists,
    notes,
    reservations,
    entries,
    settlements,
    goals,
    attachments,
    checklists,
    categories,
    visits,
    segments,
    destinations,
  };
}

function compareRows(a: RowSnapshot, b: RowSnapshot) {
  return String(a.values.sortKey ?? '').localeCompare(String(b.values.sortKey ?? ''));
}

// ── Money (mirrors PFStore Ledger) ─────────────────────────────────────────────

export function amountIn(
  e: { amountMinor: number; currency: string; rates: Record<string, string> },
  currency: string,
  minor = e.amountMinor,
) {
  if (currency === e.currency) return minor;
  const rate = e.rates[currency];
  return rate ? convert(minor, e.currency, currency, rate) : null;
}

export const myShare = (e: Entry, me: string) =>
  e.ownerUserId
    ? e.ownerUserId === me
      ? e.amountMinor
      : 0
    : e.splits.filter((s) => s.userId === me).reduce((a, s) => a + s.amountMinor, 0);

export function status(e: Entry) {
  if (e.instalments.length === 0) return e.status;
  return e.instalments.every((i) => i.paidAt) ? 'paid' : 'due';
}

export function sharedTotal(m: TripModel, currency: string) {
  let sum = 0;
  let pending = 0;
  for (const e of m.entries) {
    if (e.ownerUserId) continue;
    const v = amountIn(e, currency);
    if (v === null) pending += 1;
    else sum += v;
  }
  return { sum, pending };
}

export function myTotal(m: TripModel, me: string, currency: string) {
  let sum = 0;
  for (const e of m.entries) sum += amountIn(e, currency, myShare(e, me)) ?? 0;
  return sum;
}

/** Owed to me (positive) or by me (negative), shared expenses and settlements. */
export function balance(m: TripModel, me: string, currency: string) {
  let sum = 0;
  for (const e of m.entries) {
    if (e.ownerUserId) continue;
    const paid = e.payers.filter((p) => p.userId === me).reduce((a, p) => a + p.amountMinor, 0);
    const owed = e.splits.filter((s) => s.userId === me).reduce((a, s) => a + s.amountMinor, 0);
    sum += amountIn(e, currency, paid - owed) ?? 0;
  }
  for (const s of m.settlements) {
    const v =
      s.currency === currency
        ? s.amountMinor
        : s.rates[currency]
          ? convert(s.amountMinor, s.currency, currency, s.rates[currency])
          : null;
    if (v === null) continue;
    if (s.fromUserId === me) sum += v;
    else if (s.toUserId === me) sum -= v;
  }
  return sum;
}

/** Spend in a goal's scope for the period containing `today` (weeks start on Monday). */
export function goalProgress(m: TripModel, g: Goal, me: string, today: string) {
  const [from, to] = periodWindow(g.period, today);
  let spent = 0;
  for (const e of m.entries) {
    if (!e.dateLocal || e.dateLocal < from || e.dateLocal > to) continue;
    if (g.categoryId && e.categoryId !== g.categoryId) continue;
    const minor = g.ownerUserId ? myShare(e, me) : e.ownerUserId ? 0 : e.amountMinor;
    if (minor !== 0) spent += amountIn(e, g.currency, minor) ?? 0;
  }
  return spent;
}

function periodWindow(period: string, date: string): [string, string] {
  const d = new Date(`${date}T00:00:00Z`);
  const iso = (x: Date) => x.toISOString().slice(0, 10);
  if (period === 'day') return [date, date];
  if (period === 'week') {
    const dow = (d.getUTCDay() + 6) % 7;
    const start = new Date(d);
    start.setUTCDate(d.getUTCDate() - dow);
    const end = new Date(start);
    end.setUTCDate(start.getUTCDate() + 6);
    return [iso(start), iso(end)];
  }
  if (period === 'month') return [`${date.slice(0, 7)}-01`, `${date.slice(0, 7)}-31`];
  return ['0000-01-01', '9999-12-31'];
}

export const flag = (date: string | null) =>
  date && date.length >= 10 ? `${date.slice(5, 7)}.${date.slice(8, 10)}` : '--.--';
export const time = (local: string | null) => (local && local.length >= 16 ? local.slice(11, 16) : null);
export const today = (tz?: string | null) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: tz ?? undefined,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(tripNow()));
